import { Link } from "react-router-dom";
import { defineListView } from "@/base/list/defineListView";
import type { AuthedRequester } from "@/lib/brokerAuth";
import { listBrokerageBonds, type BrokerageBond } from "@/lib/brokerAccounts";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { IssuedBondDownload } from "@/components/BondListCells";

// The bonds Signal has issued across the brokerage (BRK-S181) as a BaseListView
// definition (BRK-S253). PMS pages it and applies nothing else
// (`listBrokerageBonds`), so there is no search or sort until PMS-S590.
//
// Six columns at most, so the form title rides under the type and the
// effective date under the bond date. A bond's row opens the order it came
// from, when it has one; its insured links to their account; the executed PDF
// downloads as the row's action.

export function issuedBondsListView({
  request,
  bondTypeLabel,
}: {
  request: AuthedRequester;
  /** The catalogue's names for bond types (BRK-S179): never a raw key. */
  bondTypeLabel: (key: string) => string;
}) {
  return defineListView<BrokerageBond>({
    endpoint: "/api/broker/bonds",
    noun: "bonds",
    title: "Bonds",
    context: "The bonds Signal has issued for your brokerage’s insureds.",
    columns: [
      {
        id: "bond",
        header: "Bond",
        kind: "title",
        // A form that maps no number still has a bond: an em dash, never
        // the word "null".
        cell: (b) => (
          <span className="tabular-nums">{b.bondNumber ?? "—"}</span>
        ),
        // The insured stays a link to its account (BRK-S181), lifted above
        // the row's stretched link so it keeps its own click.
        subtitle: (b) => (
          <Link
            to={`/surety/accounts/${b.accountId}`}
            className="relative z-[1] text-brk-accent hover:underline"
          >
            {b.accountName}
          </Link>
        ),
      },
      {
        id: "type",
        header: "Type",
        cell: (b) => (
          <>
            {bondTypeLabel(b.bondType)}
            {b.formTitle && (
              <span className="block text-xs text-brk-dim">{b.formTitle}</span>
            )}
          </>
        ),
      },
      {
        id: "bondDate",
        header: "Bond date",
        cell: (b) => (
          <>
            {b.bondDate ? formatBondDate(b.bondDate) : "—"}
            {b.effectiveDate && (
              <span className="block text-xs text-brk-dim">
                Effective {formatBondDate(b.effectiveDate)}
              </span>
            )}
          </>
        ),
      },
      { id: "obligee", header: "Obligee", cell: (b) => b.obligeeName ?? "—" },
      {
        id: "order",
        header: "Order",
        kind: "mono",
        cell: (b) => b.orderNumber ?? "—",
      },
    ],
    rowKey: (b) => b.bondId,
    rowHref: (b) => (b.orderId ? `/surety/submissions/${b.orderId}` : null),
    rowLabel: (b) => `Bond ${b.bondNumber ?? b.bondId} for ${b.accountName}`,
    rowActions: (b) => <IssuedBondDownload bond={b} />,
    pageSize: 25,
    empty: { title: "No bonds issued yet." },
    load: ({ limit, offset }) => listBrokerageBonds(request, { limit, offset }),
  });
}
